import React from 'react';
import { ActivityIndicator, ScrollView, TouchableOpacity, View } from 'react-native';
import { useRouter } from 'expo-router';
import { MaterialIcons } from '@expo/vector-icons';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import AppHeaderBar from '../../src/components/layout/AppHeaderBar';
import { useAuth } from '../../src/context/AuthContext';
import { useTranslation } from '../../src/hooks/useTranslation';
import { authAPI } from '../../src/services/api';
import { pathForPhase } from '../../src/navigation/phaseRedirect';
import { ROUTES } from '../../src/navigation/routes';
import { trackFeatureUsage } from '../../src/services/analytics';
import SampleReportViewer from '../../src/components/onboarding/SampleReportViewer';
import { brand } from '../../src/design-system/theme/tokens';

const COLORS = {
  primary: brand.primary,
  secondary: '#F59E0B',
  text: {
    primary: '#111827',
    secondary: '#4B5563',
  },
};

export default function UploadFirstSessionRoute() {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { t } = useTranslation();
  const { phase, user, refreshAuth } = useAuth();
  const [requestedRefresh, setRequestedRefresh] = React.useState(false);
  const [sawLoadingAfterRefresh, setSawLoadingAfterRefresh] = React.useState(false);
  const [intakeSummary, setIntakeSummary] = React.useState<any>(null);
  const [showSampleReport, setShowSampleReport] = React.useState(false);

  React.useEffect(() => {
    if (phase === 'authenticated') {
      setRequestedRefresh(false);
      setSawLoadingAfterRefresh(false);
      return;
    }

    if (requestedRefresh && phase === 'loading') {
      setSawLoadingAfterRefresh(true);
      return;
    }

    if (!requestedRefresh && phase !== 'loading') {
      setRequestedRefresh(true);
      void refreshAuth();
    }
  }, [phase, refreshAuth, requestedRefresh]);

  React.useEffect(() => {
    if (!requestedRefresh || !sawLoadingAfterRefresh || phase === 'loading' || phase === 'authenticated') {
      return;
    }

    router.replace(pathForPhase(phase) as any);
  }, [phase, requestedRefresh, router, sawLoadingAfterRefresh]);

  React.useEffect(() => {
    let cancelled = false;

    if (phase !== 'authenticated') {
      return () => {
        cancelled = true;
      };
    }

    void (async () => {
      try {
        const summary = await authAPI.getIntakeSummary();
        if (!cancelled) {
          setIntakeSummary(summary);
        }
      } catch (error) {
        console.warn('Failed to load intake summary for activation screen:', error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [phase]);

  const goal = intakeSummary?.primary_goal;
  const firstName = user?.full_name?.trim()?.split(' ')[0] || t('dashboard.activationGreetingName');

  if (phase !== 'authenticated') {
    return (
      <SafeAreaView className="flex-1 bg-surface">
        <View className="flex-1 items-center justify-center p-8">
          <ActivityIndicator size="large" color={COLORS.primary} />
          <Text className="mt-4 text-base text-muted-foreground">
            {t('dashboard.loadingData')}
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView className="flex-1 bg-surface">
      <ScrollView
        testID="activation_upload_scroll"
        style={{ flex: 1, minHeight: 0 }}
        contentContainerStyle={{
          flexGrow: 1,
          paddingHorizontal: 24,
          paddingTop: 24,
          paddingBottom: Math.max(insets.bottom + 32, 40),
        }}
      >
        <View className="mx-auto w-full max-w-3xl flex-1 justify-center">
          <Card className="mb-6 border-0" style={{ backgroundColor: 'rgba(30, 64, 175, 0.08)' }}>
            <CardContent className="p-6">
              <View className="mb-4 flex-row items-center">
                <View
                  className="mr-3 h-12 w-12 items-center justify-center rounded-2xl"
                  style={{ backgroundColor: COLORS.primary }}
                >
                  <MaterialIcons name="graphic-eq" size={24} color="white" />
                </View>
                <View className="flex-1">
                  <Text className="text-sm font-semibold uppercase tracking-wide" style={{ color: COLORS.primary }}>
                    {t('dashboard.activationScreenBadge')}
                  </Text>
                  <Text className="text-3xl font-semibold" style={{ color: COLORS.text.primary }}>
                    {t('dashboard.activationScreenTitle')}
                  </Text>
                </View>
              </View>
              <Text className="text-base leading-6" style={{ color: COLORS.text.secondary }}>
                {goal
                  ? t(
                      'dashboard.activationScreenGoal',
                      { firstName, goal },
                    )
                  : t(
                      'dashboard.activationScreenSubtitle'
                    )}
              </Text>
            </CardContent>
          </Card>

          <View className="mb-6 gap-3">
            <Button
              variant="default"
              size="lg"
              onPress={() => {
                trackFeatureUsage('activation_upload', 'clicked', { source: 'activation_screen' });
                router.push({
                  pathname: ROUTES.TABS_LIBRARY,
                  params: { autoAction: 'pickAudio' },
                });
              }}
            >
              <Text className="text-primary-foreground font-medium">
                {t('dashboard.activationScreenPrimaryCta')}
              </Text>
            </Button>

            <Button
              variant="outline"
              size="lg"
              onPress={() => setShowSampleReport(true)}
            >
              <Text className="font-medium" style={{ color: COLORS.primary }}>
                {t('dashboard.activationScreenSecondaryCta')}
              </Text>
            </Button>

            <Button
              variant="outline"
              size="lg"
              onPress={() => {
                trackFeatureUsage('practice_session', 'cta_tapped', { source: 'activation_screen' });
                router.push(ROUTES.ACTIVATION_PRACTICE_SESSION as any);
              }}
            >
              <Text className="font-medium text-foreground">
                {t('dashboard.activationScreenPracticeCta')}
              </Text>
            </Button>

            <TouchableOpacity
              className="items-center justify-center rounded-xl px-4 py-4"
              onPress={() => {
                trackFeatureUsage('dashboard', 'opened', { source: 'activation_screen' });
                router.replace(ROUTES.TABS_DASHBOARD);
              }}
            >
              <Text className="text-sm font-medium" style={{ color: COLORS.primary }}>
                {t('dashboard.activationScreenContinueCta')}
              </Text>
            </TouchableOpacity>
          </View>
          <Card>
            <CardContent className="p-6">
              <Text className="mb-4 text-lg font-semibold" style={{ color: COLORS.text.primary }}>
                {t('dashboard.activationScreenWhyTitle')}
              </Text>
              <View className="gap-4">
                <View className="flex-row">
                  <MaterialIcons name="check-circle" size={20} color={COLORS.primary} />
                  <Text className="ml-3 flex-1 text-sm leading-6" style={{ color: COLORS.text.secondary }}>
                    {t(
                      'dashboard.activationScreenWhyOne'
                    )}
                  </Text>
                </View>
                <View className="flex-row">
                  <MaterialIcons name="check-circle" size={20} color={COLORS.primary} />
                  <Text className="ml-3 flex-1 text-sm leading-6" style={{ color: COLORS.text.secondary }}>
                    {t(
                      'dashboard.activationScreenWhyTwo'
                    )}
                  </Text>
                </View>
                <View className="flex-row">
                  <MaterialIcons name="check-circle" size={20} color={COLORS.primary} />
                  <Text className="ml-3 flex-1 text-sm leading-6" style={{ color: COLORS.text.secondary }}>
                    {t(
                      'dashboard.activationScreenWhyThree'
                    )}
                  </Text>
                </View>
              </View>
            </CardContent>
          </Card>
        </View>
      </ScrollView>
      <SampleReportViewer
        visible={showSampleReport}
        surface="activation_screen"
        primaryAction="upload"
        onClose={() => setShowSampleReport(false)}
        onPrimaryPress={() => {
          setShowSampleReport(false);
          router.push({ pathname: ROUTES.TABS_LIBRARY, params: { autoAction: 'pickAudio' } });
        }}
      />
    </SafeAreaView>
  );
}
